/**
 * Text-to-mesh pipeline for the text generator.
 *
 * Pure synchronous functions on top of opentype.js: layout characters into
 * baseline-anchored placements, sample each glyph's bezier outline into
 * closed polylines, group those contours into (outline, holes) shapes via
 * even-odd containment depth, and feed each shape into
 * `buildExtrudedPolygon` to produce a clean PolygonTopology slab. Per-glyph
 * results are merged with `concatPrimitiveGeometries` into a single mesh.
 *
 * Coordinate conventions:
 *   • Output lives in the XY plane with extrusion along +Z.
 *   • +Y is up — opentype's screen-space Y (which points down) is negated
 *     at sample time so ascenders end up above the baseline.
 *   • Origin is the baseline-left of the first character (Blender
 *     convention).
 *   • All distances are in metres; `size` maps one em to that many metres.
 */

import type { Font, Glyph } from "opentype.js";
import {
  buildExtrudedPolygon,
  concatPrimitiveGeometries,
  emptyGeometry,
  type Point2,
} from "../generatorGeom.ts";
import type { PrimitiveGeometry } from "../../primitives.ts";

// ---------------------------------------------------------------------------
// Params
// ---------------------------------------------------------------------------

/** Inputs the layout pass needs (a strict subset of the build params). */
export interface TextLayoutParams {
  text: string;
  /** Em size in metres. One em maps to `size` metres of XY extent. */
  size: number;
  /** Extra advance per character, expressed as a fraction of an em. */
  characterSpacing: number;
  /** Multiplier of the font's intrinsic line height. 1.0 = single-spaced. */
  lineHeight: number;
}

/** Inputs the full build needs. */
export interface TextBuildParams extends TextLayoutParams {
  /** Extrusion depth along +Z, in metres. */
  extrusion: number;
}

/**
 * One placed glyph from the layout pass. Whitespace and unmapped
 * characters advance the cursor but do not produce a placement.
 */
export interface GlyphPlacement {
  glyph: Glyph;
  /** Baseline-left x in scene units (metres). */
  x: number;
  /** Baseline y in scene units (metres), +Y up. */
  y: number;
}

/** Internal: bezier sample density per curve segment. */
const CURVE_SEGMENTS = 12;

// ---------------------------------------------------------------------------
// Layout
// ---------------------------------------------------------------------------

/**
 * Walk `text`, applying per-glyph advance widths, opentype kerning, and
 * the user's character spacing / line height. Newlines reset x and step
 * the baseline down by one line.
 */
export function layoutText(p: TextLayoutParams, font: Font): GlyphPlacement[] {
  const placements: GlyphPlacement[] = [];
  const size = Math.max(0, p.size);
  if (size === 0 || p.text.length === 0) return placements;

  const unitScale = size / font.unitsPerEm;
  const spacing = p.characterSpacing * size;
  // Default leading = ascender − descender (descender is negative in
  // opentype's coordinate system).
  const leadingUnits = font.ascender - font.descender;
  const lineStep = leadingUnits * unitScale * p.lineHeight;

  let x = 0;
  let line = 0;
  let prevGlyph: Glyph | null = null;
  for (const ch of p.text) {
    if (ch === "\n") {
      x = 0;
      line += 1;
      prevGlyph = null;
      continue;
    }
    const glyph = font.charToGlyph(ch);
    if (prevGlyph) {
      x += font.getKerningValue(prevGlyph, glyph) * unitScale;
    }
    const y = -line * lineStep;
    if (glyph.path.commands.length > 0) {
      placements.push({ glyph, x, y });
    }
    x += (glyph.advanceWidth ?? 0) * unitScale + spacing;
    prevGlyph = glyph;
  }
  return placements;
}

// ---------------------------------------------------------------------------
// Glyph → contours
// ---------------------------------------------------------------------------

/**
 * Sample a glyph's bezier outline into a list of closed polylines
 * (one per subpath), in scene-space metres.
 *
 * opentype's path commands are walked in order; M starts a new contour,
 * L appends a vertex, Q and C are sampled with `CURVE_SEGMENTS`
 * subdivisions via De Casteljau, and Z closes the current contour.
 * Trailing duplicates of the start point (some fonts emit them) are
 * dropped so winding tests aren't fooled by collinear repeat vertices.
 *
 * Y is negated to convert opentype's screen-space Y-down to scene-space
 * Y-up.
 */
export function glyphToContours(glyph: Glyph, pixelSize: number): Point2[][] {
  const path = glyph.getPath(0, 0, pixelSize);
  const contours: Point2[][] = [];
  let current: Point2[] = [];
  let cx = 0;
  let cy = 0;
  let startX = 0;
  let startY = 0;

  const closeContour = () => {
    if (current.length >= 3) {
      const last = current[current.length - 1];
      const first = current[0];
      if (
        Math.abs(last[0] - first[0]) < 1e-9 &&
        Math.abs(last[1] - first[1]) < 1e-9
      ) {
        current.pop();
      }
      if (current.length >= 3) contours.push(current);
    }
    current = [];
  };

  for (const cmd of path.commands) {
    switch (cmd.type) {
      case "M": {
        closeContour();
        const px = cmd.x;
        const py = -cmd.y;
        current = [[px, py]];
        startX = px;
        startY = py;
        cx = px;
        cy = py;
        break;
      }
      case "L": {
        const px = cmd.x;
        const py = -cmd.y;
        current.push([px, py]);
        cx = px;
        cy = py;
        break;
      }
      case "Q": {
        const x1 = cmd.x1;
        const y1 = -cmd.y1;
        const x2 = cmd.x;
        const y2 = -cmd.y;
        for (let i = 1; i <= CURVE_SEGMENTS; i++) {
          const t = i / CURVE_SEGMENTS;
          const u = 1 - t;
          const px = u * u * cx + 2 * u * t * x1 + t * t * x2;
          const py = u * u * cy + 2 * u * t * y1 + t * t * y2;
          current.push([px, py]);
        }
        cx = x2;
        cy = y2;
        break;
      }
      case "C": {
        const x1 = cmd.x1;
        const y1 = -cmd.y1;
        const x2 = cmd.x2;
        const y2 = -cmd.y2;
        const x3 = cmd.x;
        const y3 = -cmd.y;
        for (let i = 1; i <= CURVE_SEGMENTS; i++) {
          const t = i / CURVE_SEGMENTS;
          const u = 1 - t;
          const u2 = u * u;
          const t2 = t * t;
          const px =
            u2 * u * cx + 3 * u2 * t * x1 + 3 * u * t2 * x2 + t2 * t * x3;
          const py =
            u2 * u * cy + 3 * u2 * t * y1 + 3 * u * t2 * y2 + t2 * t * y3;
          current.push([px, py]);
        }
        cx = x3;
        cy = y3;
        break;
      }
      case "Z":
        closeContour();
        cx = startX;
        cy = startY;
        break;
    }
  }
  closeContour();
  return contours;
}

// ---------------------------------------------------------------------------
// Hole partition
// ---------------------------------------------------------------------------

/**
 * Group raw glyph contours into a list of `{ outline, holes }` shapes
 * via even-odd containment depth.
 *
 * For each contour we compute its representative interior point and
 * count how many other contours strictly contain it: even depth → outer
 * outline, odd depth → hole of the smallest enclosing contour. Nested
 * islands (depth ≥ 2, e.g. a contour inside a counter inside an outer
 * outline) become their own outline shapes, with their own potential
 * holes.
 *
 * Winding-based detection is intentionally avoided: glyph contour
 * winding flips between TrueType and CFF outlines, and again under our
 * Y-flip, so containment depth is the only conventionally robust signal.
 */
export function partitionContours(
  contours: readonly Point2[][],
): Array<{ outline: Point2[]; holes: Point2[][] }> {
  const n = contours.length;
  if (n === 0) return [];
  const reps: Point2[] = contours.map((c) => interiorPoint(c));

  const depth = new Array<number>(n).fill(0);
  const parent = new Array<number>(n).fill(-1);
  for (let i = 0; i < n; i++) {
    let bestArea = Infinity;
    for (let j = 0; j < n; j++) {
      if (i === j) continue;
      if (pointInPolygon(reps[i], contours[j])) {
        depth[i]++;
        const a = Math.abs(signedArea(contours[j]));
        if (a < bestArea) {
          bestArea = a;
          parent[i] = j;
        }
      }
    }
  }

  const shapes: Array<{ outline: Point2[]; holes: Point2[][] }> = [];
  const outlineIndex = new Map<number, number>();
  for (let i = 0; i < n; i++) {
    if (depth[i] % 2 === 0) {
      outlineIndex.set(i, shapes.length);
      shapes.push({ outline: [...contours[i]], holes: [] });
    }
  }
  for (let i = 0; i < n; i++) {
    if (depth[i] % 2 === 1 && parent[i] >= 0) {
      const idx = outlineIndex.get(parent[i]);
      if (idx !== undefined) shapes[idx].holes.push([...contours[i]]);
    }
  }
  return shapes;
}

/**
 * Return a point that lies strictly inside a simple polygon. Picks the
 * leftmost vertex (guaranteed on the convex hull) and averages it with
 * its two neighbours so the result is a small inward step from the
 * corner — reliably interior for glyph contours, and cheap.
 */
function interiorPoint(poly: readonly Point2[]): Point2 {
  if (poly.length < 3) return poly[0] ?? [0, 0];
  let imin = 0;
  for (let i = 1; i < poly.length; i++) {
    if (poly[i][0] < poly[imin][0]) imin = i;
  }
  const prev = poly[(imin - 1 + poly.length) % poly.length];
  const cur = poly[imin];
  const next = poly[(imin + 1) % poly.length];
  return [(prev[0] + cur[0] + next[0]) / 3, (prev[1] + cur[1] + next[1]) / 3];
}

/** Standard ray-casting point-in-polygon test on a closed simple polygon. */
function pointInPolygon(p: Point2, poly: readonly Point2[]): boolean {
  let inside = false;
  const n = poly.length;
  const [px, py] = p;
  for (let i = 0, j = n - 1; i < n; j = i++) {
    const [xi, yi] = poly[i];
    const [xj, yj] = poly[j];
    const intersect =
      yi > py !== yj > py &&
      px < ((xj - xi) * (py - yi)) / (yj - yi || 1e-30) + xi;
    if (intersect) inside = !inside;
  }
  return inside;
}

/** Signed polygon area. Positive = CCW, negative = CW. */
function signedArea(poly: readonly Point2[]): number {
  let a = 0;
  const n = poly.length;
  for (let i = 0, j = n - 1; i < n; j = i++) {
    a += (poly[j][0] - poly[i][0]) * (poly[j][1] + poly[i][1]);
  }
  return -0.5 * a;
}

// ---------------------------------------------------------------------------
// Build
// ---------------------------------------------------------------------------

/**
 * Build a single PrimitiveGeometry containing every glyph in the text,
 * extruded to `extrusion` metres along +Z.
 *
 * Returns `emptyGeometry()` when the text is empty or contains no
 * renderable glyphs. Each shape (an outer outline + its holes) becomes
 * one extruded slab via `buildExtrudedPolygon`; all slabs are merged
 * with `concatPrimitiveGeometries` into the final mesh.
 */
export function buildTextGeometry(
  p: TextBuildParams,
  font: Font,
): PrimitiveGeometry {
  const placements = layoutText(p, font);
  if (placements.length === 0) return emptyGeometry();

  const thickness = Math.max(0, p.extrusion);
  const parts: PrimitiveGeometry[] = [];

  for (const placement of placements) {
    const contours = glyphToContours(placement.glyph, p.size);
    const shapes = partitionContours(contours);
    for (const shape of shapes) {
      const outline = shape.outline.map(
        ([x, y]) => [x + placement.x, y + placement.y] as Point2,
      );
      const holes = shape.holes.map((h) =>
        h.map(([x, y]) => [x + placement.x, y + placement.y] as Point2),
      );
      parts.push(buildExtrudedPolygon(outline, thickness, holes));
    }
  }

  if (parts.length === 0) return emptyGeometry();
  return concatPrimitiveGeometries(parts);
}
